import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertCircle, 
  Clock, 
  User, 
  Search, 
  Filter,
  Calendar,
  ArrowRight,
  MoreVertical,
  Pencil
} from 'lucide-react';
import { Solicitacao } from '@/types/solicitacao';
import { TicketStatus, STATUS_LABELS, STATUS_COLORS } from '@/utils/statusTransitions';
import { formatBrazilTime } from '@/utils/timezone';
import { getSLAStatus, getUrgenciaColor } from '@/utils/slaStatus';
import { useIncidentes } from '@/hooks/useIncidentes';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface IncidentesKanbanProps {
  incidentes: Solicitacao[];
  onEditIncidente: (incidente: Solicitacao) => void;
}

interface KanbanColumn {
  id: TicketStatus;
  title: string;
  color: string;
  bgColor: string;
  count: number;
}

const IncidentesKanban = ({ incidentes, onEditIncidente }: IncidentesKanbanProps) => {
  const { updateIncidente } = useIncidentes();
  const [searchTerm, setSearchTerm] = useState('');
  const [urgenciaFilter, setUrgenciaFilter] = useState<string>('all');
  const [clienteFilter, setClienteFilter] = useState<string>('all');
  const [draggedItem, setDraggedItem] = useState<Solicitacao | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<TicketStatus | null>(null);

  // Definir colunas do Kanban
  const columns: KanbanColumn[] = [
    {
      id: 'aberta',
      title: 'Aberta',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50 border-blue-200',
      count: 0
    },
    {
      id: 'em_andamento',
      title: 'Em Andamento',
      color: 'text-yellow-700',
      bgColor: 'bg-yellow-50 border-yellow-200',
      count: 0
    },
    {
      id: 'pendente',
      title: 'Pendente',
      color: 'text-orange-700',
      bgColor: 'bg-orange-50 border-orange-200',
      count: 0
    },
    {
      id: 'resolvida',
      title: 'Resolvida',
      color: 'text-green-700',
      bgColor: 'bg-green-50 border-green-200',
      count: 0
    },
    {
      id: 'fechada',
      title: 'Fechada',
      color: 'text-gray-700',
      bgColor: 'bg-gray-50 border-gray-200',
      count: 0
    }
  ];

  // Filtrar incidentes
  const filteredIncidentes = useMemo(() => {
    return incidentes.filter(incidente => {
      const matchesSearch = !searchTerm || 
        incidente.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incidente.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incidente.solicitante?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesUrgencia = urgenciaFilter === 'all' || incidente.urgencia === urgenciaFilter;
      const matchesCliente = clienteFilter === 'all' || incidente.client_id === clienteFilter;
      
      return matchesSearch && matchesUrgencia && matchesCliente;
    });
  }, [incidentes, searchTerm, urgenciaFilter, clienteFilter]);

  // Agrupar incidentes por status
  const groupedIncidentes = useMemo(() => {
    const grouped = filteredIncidentes.reduce((acc, incidente) => {
      const status = incidente.status as TicketStatus;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(incidente);
      return acc;
    }, {} as Record<TicketStatus, Solicitacao[]>);

    // Atualizar contadores das colunas
    columns.forEach(column => {
      column.count = grouped[column.id]?.length || 0;
    });

    return grouped;
  }, [filteredIncidentes]);

  // Obter clientes únicos para filtro
  const uniqueClientes = useMemo(() => {
    const clientes = incidentes
      .map(inc => inc.cliente)
      .filter((cliente): cliente is { id: string; name: string } => 
        !!cliente && 'id' in cliente && !!cliente.id
      )
      .filter((cliente, index, self) => 
        self.findIndex(c => c.id === cliente.id) === index
      );
    return clientes;
  }, [incidentes]);

  // Handlers para drag and drop
  const handleDragStart = (e: React.DragEvent, incidente: Solicitacao) => {
    setDraggedItem(incidente);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, columnId: TicketStatus) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e: React.DragEvent, newStatus: TicketStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (!draggedItem || draggedItem.status === newStatus) {
      setDraggedItem(null);
      return;
    }

    try {
      await updateIncidente.mutateAsync({
        id: draggedItem.id,
        data: { status: newStatus }
      });
    } catch (error) {
      console.error('Erro ao atualizar status do incidente:', error);
    }
    
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverColumn(null);
  };

  // Componente do card do incidente
  const IncidenteCard = ({ incidente }: { incidente: Solicitacao }) => {
    const slaStatus = getSLAStatus(incidente);
    const IconComponent = slaStatus.icon;
    const isDragging = draggedItem?.id === incidente.id;

    return (
      <Card 
        className={`mb-3 cursor-move transition-all duration-200 hover:shadow-md ${
          isDragging ? 'opacity-50 rotate-2 scale-105' : ''
        }`}
        draggable
        onDragStart={(e) => handleDragStart(e, incidente)}
        onDragEnd={handleDragEnd}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-sm text-blue-600">
                  {incidente.numero}
                </span>
                <Badge className={getUrgenciaColor(incidente.urgencia)}>
                  {incidente.urgencia}
                </Badge>
              </div>
              <h4 className="font-medium text-sm line-clamp-2 mb-2">
                {incidente.titulo}
              </h4>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEditIncidente(incidente)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Editar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="space-y-2 text-xs text-gray-600">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {incidente.solicitante?.name || 'N/A'}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatBrazilTime(incidente.data_abertura, 'dd/MM')}
              </span>
            </div>
            
            {incidente.cliente && (
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Cliente:</span>
                <span className="font-medium">{incidente.cliente.name}</span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <IconComponent className="h-3 w-3" />
                <Badge className={`${slaStatus.color} text-xs`}>
                  {slaStatus.label}
                </Badge>
              </div>
              {incidente.grupo_responsavel && (
                <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                  {incidente.grupo_responsavel.name}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por título, número ou solicitante..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={urgenciaFilter} onValueChange={setUrgenciaFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por urgência" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as urgências</SelectItem>
                <SelectItem value="baixa">Baixa</SelectItem>
                <SelectItem value="media">Média</SelectItem>
                <SelectItem value="alta">Alta</SelectItem>
                <SelectItem value="critica">Crítica</SelectItem>
              </SelectContent>
            </Select>

            <Select value={clienteFilter} onValueChange={setClienteFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                {uniqueClientes.map((cliente) => (
                  <SelectItem key={cliente.id} value={cliente.id}>
                    {cliente.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Board Kanban */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 min-h-[600px]">
        {columns.map((column) => (
          <div
            key={column.id}
            className={`${column.bgColor} border-2 rounded-lg p-4 ${
              dragOverColumn === column.id ? 'border-blue-400 bg-blue-100' : ''
            }`}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, column.id)}
          >
            <div className={`flex items-center justify-between mb-4 ${column.color}`}>
              <h3 className="font-semibold text-sm uppercase tracking-wide">
                {column.title}
              </h3>
              <Badge variant="secondary" className="text-xs">
                {column.count}
              </Badge>
            </div>

            <div className="space-y-3">
              {groupedIncidentes[column.id]?.map((incidente) => (
                <IncidenteCard key={incidente.id} incidente={incidente} />
              ))}
              
              {(!groupedIncidentes[column.id] || groupedIncidentes[column.id].length === 0) && (
                <div className="text-center py-8 text-gray-400">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhum incidente</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Estatísticas resumidas */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Total de incidentes: <strong>{filteredIncidentes.length}</strong>
            </span>
            <span>
              Em andamento: <strong>{(groupedIncidentes['aberta']?.length || 0) + (groupedIncidentes['em_andamento']?.length || 0) + (groupedIncidentes['pendente']?.length || 0)}</strong>
            </span>
            <span>
              Resolvidos: <strong>{(groupedIncidentes['resolvida']?.length || 0) + (groupedIncidentes['fechada']?.length || 0)}</strong>
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IncidentesKanban;
